import type { ReactNode } from 'react';
import { useState } from 'react';

import {
  Divider,
  Flex,
  Header,
  IconButton,
  IconDelete,
  IconEdit,
  IconStarSolid,
  InputLabel,
  LabelledToggle,
  PrimaryButton,
  Select,
  Space,
  SubText,
  TertiaryButton,
  WithOverlayLoader,
  WithTooltip,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { ExporterExportFormStyled } from './styles/ExporterExportFormStyled'
import { type IExporterProps } from '../../Exporter'
import { type ExportFormat } from '../../types'
import { ExportPreview } from '../ExportPreview'
import { type IExportPreviewProps } from '../ExportPreview/ExportPreview'

export interface IExporterExportFormProps {
  selectedFormat?: ExportFormat
  formatOptions?: ExportFormat[]
  isDefaultFormat?: boolean
  isFetchingSampleData?: boolean
  isFormatEditable?: boolean
  isSubmitting?: boolean
  renderFilters?: ReactNode
  sampleData?: IExportPreviewProps['sampleData']
  additionalToggles?: IExporterProps['additionalToggles']
  userEmail?: IExporterProps['userEmail']
  emailExportMode?: IExporterProps['emailExportMode']
  onFormatSelect?: (value: ExportFormat) => void
  onSetDefaultFormat?: () => void
  onCreateNewFormat?: () => void
  onEditFormat?: () => void
  onDeleteFormat?: () => void
  onCancel?: () => void
  onSubmit?: (data: { exportEmail: boolean }) => void
}

export interface ExporterCustomToggleValue {
  value: boolean
  label: string
}

const isExportable = (sampleData: IExportPreviewProps['sampleData']) => {
  return sampleData?.length > 1
}

export const ExporterExportForm = ({
  selectedFormat = null,
  formatOptions = [],
  isDefaultFormat = true,
  isFetchingSampleData = false,
  isFormatEditable = false,
  isSubmitting = false,
  renderFilters,
  sampleData = [],
  additionalToggles = [],
  userEmail,
  emailExportMode,
  onFormatSelect = () => undefined,
  onSetDefaultFormat = () => undefined,
  onCreateNewFormat = () => undefined,
  onEditFormat = () => undefined,
  onDeleteFormat = () => undefined,
  onCancel = () => undefined,
  onSubmit = () => undefined,
}: IExporterExportFormProps) => {
  const intl = useProcurifyIntl()

  const [exportEmail, setExportEmail] = useState(true)
  const [customToggleValues, setCustomToggleValues] = useState<{
    [paramKey: string]: ExporterCustomToggleValue
  }>(
    additionalToggles.reduce((prev, toggle) => {
      prev[toggle.key] = {
        value: toggle.defaultValue || false,
        label: toggle.label,
      }
      return prev
    }, {})
  )

  const onCustomToggle = (toggleKey: string) => {
    setCustomToggleValues((currentValue) => {
      return {
        ...currentValue,
        [toggleKey]: {
          ...currentValue[toggleKey],
          value: !currentValue[toggleKey].value,
        },
      }
    })
  }

  const _onSubmit = () => {
    const customToggleData = Object.keys(customToggleValues).reduce(
      (prev, toggleKey) => {
        prev[toggleKey] = customToggleValues[toggleKey].value
        return prev
      },
      {}
    )

    onSubmit({
      exportEmail,
      ...customToggleData,
    })
  }

  const renderExportEmailSection = () => {
    return (
      <>
        {emailExportMode ? (
          <Space direction='horizontal' size='small' align='center'>
            <SubText inline>
              {userEmail
                ? intl.formatMessage(messages.emailCSVToRecipient, {
                    recipient: userEmail,
                    strong: (text) => <strong>{text}</strong>,
                  })
                : intl.formatMessage(messages.emailCSVToEmail)}
            </SubText>
          </Space>
        ) : (
          <label key='email-csv-toggle'>
            <Space direction='horizontal' size='small' align='center'>
              <LabelledToggle
                checked={exportEmail}
                onClick={() => setExportEmail(!exportEmail)}
              />
              <SubText inline>
                {intl.formatMessage(messages.emailExportLabel)}
              </SubText>
            </Space>
          </label>
        )}
      </>
    )
  }

  return (
    <ExporterExportFormStyled>
      {renderFilters && (
        <>
          <Header as='h5' gutter>
            {intl.formatMessage(messages.exportFiltersTitle)}
          </Header>
          {renderFilters}
          <Divider space={1.5} />
        </>
      )}
      <Space>
        <Header as='h5'>
          {intl.formatMessage(messages.exportFormatPreviewTitle)}
        </Header>

        <Flex.Row alignItems='flex-end'>
          <Flex.Item width={250}>
            <InputLabel htmlFor='format'>
              {intl.formatMessage(messages.ExporterExportFormatSelectLabel)}
            </InputLabel>
            <Select
              inputId='format'
              value={selectedFormat}
              placeholder={intl.formatMessage(
                messages.ExporterExportFormatSelectPlaceholder
              )}
              options={formatOptions}
              onChange={(value) => onFormatSelect(value)}
              stickies={({ closeMenu }) => {
                return [
                  <Select.StickyOption
                    key={0}
                    onClick={() => {
                      onCreateNewFormat()
                      closeMenu()
                    }}
                  >
                    {intl.formatMessage(messages.createFormatOptionLabel)}
                  </Select.StickyOption>,
                ]
              }}
              getOptionLabel={(opt) => opt.name}
              getOptionValue={(opt) => opt.id.toString()}
              isLoading={formatOptions.length === 0}
            />
          </Flex.Item>

          <Flex.Item ml='auto'>
            <Space direction='horizontal' size='small'>
              {isFormatEditable && (
                <WithTooltip
                  placement='top'
                  title={intl.formatMessage(messages.deleteFormatTitle)}
                >
                  <IconButton
                    htmlTitle={intl.formatMessage(messages.deleteFormatTitle)}
                    aria-label={intl.formatMessage(messages.deleteFormatTitle)}
                    onClick={onDeleteFormat}
                    type='button'
                    iconComponent={<IconDelete />}
                  />
                </WithTooltip>
              )}
              {isFormatEditable && (
                <WithTooltip
                  placement='top'
                  title={intl.formatMessage(messages.editFormatTitle)}
                >
                  <IconButton
                    htmlTitle={intl.formatMessage(messages.editFormatTitle)}
                    aria-label={intl.formatMessage(messages.editFormatTitle)}
                    onClick={onEditFormat}
                    type='button'
                    iconComponent={<IconEdit />}
                  />
                </WithTooltip>
              )}

              <WithTooltip
                placement='top'
                title={intl.formatMessage(messages.setDefaultFormatTitle)}
              >
                <IconButton
                  htmlTitle={intl.formatMessage(messages.setDefaultFormatTitle)}
                  aria-label={intl.formatMessage(
                    messages.setDefaultFormatTitle
                  )}
                  onClick={onSetDefaultFormat}
                  type='button'
                  iconComponent={
                    <IconStarSolid
                      color={isDefaultFormat ? 'secondary' : 'textDisabled'}
                    />
                  }
                />
              </WithTooltip>
            </Space>
          </Flex.Item>
        </Flex.Row>

        <WithOverlayLoader
          visible={isFetchingSampleData}
          text={intl.formatMessage(messages.loadingSampleDataText)}
        >
          <ExportPreview sampleData={sampleData} />
        </WithOverlayLoader>

        <Space direction='horizontal'>
          {renderExportEmailSection()}

          {additionalToggles.map((toggle) => (
            <label key={toggle.key}>
              <Space direction='horizontal' size='small' align='center'>
                <LabelledToggle
                  checked={customToggleValues[toggle.key]?.value}
                  onClick={(e) => onCustomToggle(toggle.key)}
                />
                <SubText inline>{customToggleValues[toggle.key].label}</SubText>
              </Space>
            </label>
          ))}
        </Space>

        <Divider space={0.5} />

        <Space direction='horizontal' justify='end'>
          <TertiaryButton onClick={onCancel}>
            {intl.formatMessage(messages.cancelButtonText)}
          </TertiaryButton>
          <PrimaryButton
            isLoading={isSubmitting}
            disabled={
              !isExportable(sampleData) || isFetchingSampleData || isSubmitting
            }
            onClick={_onSubmit}
            id={`export-${selectedFormat?.name.toLowerCase().replace(/ /g, '-')}`}
          >
            {intl.formatMessage(messages.exportButtonText)}
          </PrimaryButton>
        </Space>
      </Space>
    </ExporterExportFormStyled>
  );
}

ExporterExportForm.displayName = 'ExporterExportForm'
